/**
 * Centralized error handling utilities
 */

import { logger } from './logger';

export enum ErrorCode {
  // Configuration errors
  INVALID_CONFIG = 'INVALID_CONFIG',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
  
  // Initialization errors
  INITIALIZATION_FAILED = 'INITIALIZATION_FAILED',
  ALREADY_INITIALIZED = 'ALREADY_INITIALIZED',
  NOT_INITIALIZED = 'NOT_INITIALIZED',
  
  // Communication errors
  MESSAGE_TIMEOUT = 'MESSAGE_TIMEOUT',
  MESSAGE_BRIDGE_DESTROYED = 'MESSAGE_BRIDGE_DESTROYED',
  INVALID_MESSAGE = 'INVALID_MESSAGE',
  IFRAME_COMMUNICATION_ERROR = 'IFRAME_COMMUNICATION_ERROR',
  
  // DOM errors
  ELEMENT_NOT_FOUND = 'ELEMENT_NOT_FOUND',
  DOM_OPERATION_FAILED = 'DOM_OPERATION_FAILED',
  
  // Network errors
  NETWORK_ERROR = 'NETWORK_ERROR',
  API_ERROR = 'API_ERROR',
  
  // Generic errors
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  INVALID_ARGUMENT = 'INVALID_ARGUMENT',
  OPERATION_FAILED = 'OPERATION_FAILED'
}

export interface ErrorContext {
  component?: string;
  method?: string;
  data?: Record<string, unknown>;
  originalError?: Error | unknown;
}

export class OrchestratorError extends Error {
  public readonly code: ErrorCode;
  public readonly context: ErrorContext;
  public readonly timestamp: Date;

  constructor(
    code: ErrorCode, 
    message: string, 
    context: ErrorContext = {}
  ) {
    super(message);
    this.name = 'OrchestratorError';
    this.code = code;
    this.context = context;
    this.timestamp = new Date();

    // Maintain proper stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, OrchestratorError);
    }

    // Include original error stack if available
    if (context.originalError instanceof Error) {
      this.stack = `${this.stack}\nCaused by: ${context.originalError.stack}`;
    }
  }

  /**
   * Convert to JSON for serialization
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      context: this.context,
      timestamp: this.timestamp.toISOString(),
      stack: this.stack
    };
  }

  /**
   * Create user-friendly error message
   */
  getUserMessage(): string {
    switch (this.code) {
      case ErrorCode.INVALID_CONFIG:
        return 'Configuration is invalid. Please check your settings.';
      case ErrorCode.INITIALIZATION_FAILED:
        return 'Failed to initialize the chat interface. Please try again.';
      case ErrorCode.MESSAGE_TIMEOUT:
        return 'Request timed out. Please check your connection and try again.';
      case ErrorCode.IFRAME_COMMUNICATION_ERROR:
        return 'Unable to communicate with the chat interface. Please refresh the page.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }
}

/**
 * Error handling utilities
 */
export class ErrorHandler {
  /**
   * Wrap a function with error handling
   */
  static wrap<T extends (...args: unknown[]) => unknown>(
    fn: T,
    context: Omit<ErrorContext, 'originalError'>
  ): T {
    return ((...args: Parameters<T>) => {
      try {
        const result = fn(...args);
        
        // Handle async functions
        if (result instanceof Promise) {
          return result.catch(error => {
            ErrorHandler.handle(error, context);
            throw error;
          });
        }
        
        return result;
      } catch (error) {
        ErrorHandler.handle(error, context);
        throw error;
      }
    }) as T;
  }

  /**
   * Handle and log errors consistently
   */
  static handle(
    error: Error | unknown, 
    context: ErrorContext = {}
  ): OrchestratorError {
    let orchestratorError: OrchestratorError;

    if (error instanceof OrchestratorError) {
      orchestratorError = error;
    } else if (error instanceof Error) {
      orchestratorError = new OrchestratorError(
        ErrorCode.UNKNOWN_ERROR,
        error.message,
        { ...context, originalError: error }
      );
    } else {
      orchestratorError = new OrchestratorError(
        ErrorCode.UNKNOWN_ERROR,
        String(error),
        { ...context, originalError: error }
      );
    }

    // Log the error
    logger.errorWithContext(
      `Error in ${context.component || 'Unknown'}.${context.method || 'Unknown'}`,
      orchestratorError,
      context.data
    );

    return orchestratorError;
  }

  /**
   * Create configuration validation error
   */
  static createConfigError(
    message: string,
    field?: string,
    value?: unknown
  ): OrchestratorError {
    return new OrchestratorError(
      ErrorCode.INVALID_CONFIG,
      message,
      {
        component: 'ConfigManager',
        data: { field, value }
      }
    );
  }

  /**
   * Create initialization error
   */
  static createInitError(
    message: string,
    component: string,
    originalError?: Error | unknown
  ): OrchestratorError {
    return new OrchestratorError(
      ErrorCode.INITIALIZATION_FAILED,
      message,
      {
        component,
        originalError
      }
    );
  }

  /**
   * Create message communication error
   */
  static createMessageError(
    message: string,
    type?: string,
    originalError?: Error | unknown
  ): OrchestratorError {
    return new OrchestratorError(
      ErrorCode.IFRAME_COMMUNICATION_ERROR,
      message,
      {
        component: 'MessageBridge',
        data: { messageType: type },
        originalError
      }
    );
  }

  /**
   * Safe error conversion to string
   */
  static toString(error: Error | unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    return String(error);
  }

  /**
   * Check if error is recoverable
   */
  static isRecoverable(error: Error | unknown): boolean {
    if (!(error instanceof OrchestratorError)) {
      return false;
    }

    const recoverableCodes = [
      ErrorCode.MESSAGE_TIMEOUT,
      ErrorCode.NETWORK_ERROR,
      ErrorCode.API_ERROR
    ];

    return recoverableCodes.includes(error.code);
  }
}

/**
 * Decorator for automatic error handling
 */
export function handleErrors(
  component: string, 
  method?: string
) {
  return function (
    target: unknown, 
    propertyKey: string, 
    descriptor: PropertyDescriptor
  ): PropertyDescriptor {
    const originalMethod = descriptor.value;
    const methodName = method || propertyKey;

    descriptor.value = function (...args: unknown[]) {
      try {
        const result = originalMethod.apply(this, args);
        
        if (result instanceof Promise) {
          return result.catch(error => {
            throw ErrorHandler.handle(error, { component, method: methodName });
          });
        }
        
        return result;
      } catch (error) {
        throw ErrorHandler.handle(error, { component, method: methodName });
      }
    };

    return descriptor;
  };
}